import React from 'react';
import { Box, Card, CardContent, Grid, Typography, Chip } from '@mui/material';
import {
  COVERAGE_CHIP_COLORS,
  OPS_COLORS,
} from '../../constants/operationsDashboardColors';

interface KanbanRow {
  id: string;
  employeeId?: string;
  agentName: string;
  campaignName: string;
  scheduledStartTime?: string | null;
  scheduledEndTime?: string | null;
  clockInTime?: string | null;
  attendanceStatus: string;
  attendanceDetails?: string;
  coverageStatus: string;
  coveredById?: string | null;
  coveredByName?: string | null;
  absenceReasonLabel?: string;
  requiresCoverage?: boolean;
  isCoveringOthers?: boolean;
  coveringFor?: string[];
}

const COLUMNS = [
  { key: 'PRESENT', title: 'Present (on time)', color: OPS_COLORS.present },
  { key: 'LATE', title: 'Present (Late)', color: OPS_COLORS.late },
  { key: 'ABSENT', title: 'Absent', color: OPS_COLORS.critical },
  { key: 'COVERING', title: 'Covering', color: OPS_COLORS.coverage },
  { key: 'UNCOVERED', title: 'Uncovered', color: OPS_COLORS.uncovered },
];

function columnForRow(row: KanbanRow): string {
  if (row.coverageStatus === 'NOT_COVERED' && row.requiresCoverage) return 'UNCOVERED';
  if (['ABSENT', 'NO_CALL_NO_SHOW', 'SCHEDULED_OFF'].includes(row.attendanceStatus)) return 'ABSENT';
  if (row.attendanceStatus === 'LATE' || row.attendanceStatus === 'SLIGHTLY_LATE') return 'LATE';
  if (row.attendanceStatus === 'PRESENT') return 'PRESENT';
  return 'PRESENT';
}

const CoverageKanbanView: React.FC<{ rows: KanbanRow[] }> = ({ rows }) => {
  const coveringCards = Array.from(
    rows.reduce((map, row) => {
      if (!row.coveredById || !row.coveredByName) return map;
      const existing = map.get(row.coveredById) || {
        id: row.coveredById,
        agentName: row.coveredByName,
        campaignName: row.campaignName,
        covering: [] as string[],
      };
      existing.covering.push(row.agentName);
      map.set(row.coveredById, existing);
      return map;
    }, new Map<string, { id: string; agentName: string; campaignName: string; covering: string[] }>()).values()
  );

  return (
    <Grid container spacing={2}>
      {COLUMNS.map((column) => {
        const columnRows = column.key === 'COVERING'
          ? coveringCards
          : rows.filter((row) => columnForRow(row) === column.key);

        return (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={column.key}>
            <Box sx={{ bgcolor: '#f7f9fc', borderRadius: 2, p: 1.5, minHeight: 280 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: column.color, fontWeight: 700 }}>
                {column.title} ({columnRows.length})
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {column.key === 'COVERING'
                  ? coveringCards.map((card) => (
                    <Card key={card.id} variant="outlined">
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="body2" fontWeight={700}>{card.agentName}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">Covering:</Typography>
                        {card.covering.map((name) => (
                          <Chip key={name} size="small" label={name} sx={{ mr: 0.5, mt: 0.5 }} />
                        ))}
                      </CardContent>
                    </Card>
                  ))
                  : columnRows.map((row: any) => (
                    <Card key={row.id} variant="outlined">
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="body2" fontWeight={700}>{row.agentName}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">{row.campaignName}</Typography>
                        <Typography variant="caption" display="block">
                          {row.scheduledStartTime} - {row.scheduledEndTime}
                        </Typography>
                        {row.attendanceDetails && (
                          <Typography variant="caption" display="block" color="primary">{row.attendanceDetails}</Typography>
                        )}
                        {row.absenceReasonLabel && (
                          <Typography variant="caption" display="block">{row.absenceReasonLabel}</Typography>
                        )}
                        {row.coveredByName && (
                          <Typography variant="caption" color="primary" display="block">
                            ↓ Covered by {row.coveredByName}
                          </Typography>
                        )}
                        {row.isCoveringOthers && row.coveringFor?.length > 0 && (
                          <Typography variant="caption" color="secondary" display="block">
                            Covering: {row.coveringFor.join(', ')}
                          </Typography>
                        )}
                        <Chip
                          size="small"
                          label={row.coverageStatus.replace(/_/g, ' ')}
                          sx={{
                            mt: 0.5,
                            bgcolor: COVERAGE_CHIP_COLORS[row.coverageStatus] || OPS_COLORS.scheduledOff,
                            color: '#fff',
                          }}
                        />
                      </CardContent>
                    </Card>
                  ))}
                {columnRows.length === 0 && (
                  <Typography variant="caption" color="text.secondary">No agents</Typography>
                )}
              </Box>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default CoverageKanbanView;
